import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { SearchIcon, MousePointerClickIcon, XIcon, LayersIcon, AlertTriangleIcon, RouteIcon, EyeIcon, EyeOffIcon, ArrowRightIcon, GaugeIcon, GitBranchIcon, TagIcon, RulerIcon, SignalIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { LinkRouteDrawer } from '../components/LinkrouteDrawer';
import { CAPACITY_TIERS, UNKNOWN_TIER, getCapacityTier, getCapacityLabel } from '../utils/linkCapacity';
import { getAlproColor, getAlproKind, getAlproLabel, isMetro } from '../utils/alpro';
import { distanceKm } from '../utils/geo';
import { fetchTerrainProfile, type TerrainProfile as TerrainProfileData } from '../utils/elevation';
import { TerrainProfile } from '../components/TerrainProfile';
import { LossCheckPanel } from '../components/LossCheckPanel';
import { fetchUpstreamTree } from '../utils/upstream';
import { withGlobalLoading } from '../store/loadingStore';
const MAX_SELECTION = 10;
const MAX_HOPS = 12;
const LABEL_MIN_ZOOM = 10;
const ROUTE_PAGE_SIZE = 1000;
const NEARBY_RADIUS_KM = 50;
/** Keyword pendek mencocokkan ribuan site dan membekukan render. */
const MIN_SEARCH_CHARS = 3;
/** Batas marker yang digambar sekaligus agar Leaflet tetap responsif. */
const MAX_VISIBLE_NODES = 400;
/** Batas jumlah site hasil pencarian yang menjadi pusat radius. */
const MAX_SEARCH_CENTERS = 80;

/* ========================= PARSE COORD ========================= */
const parseCoord = (val: any) => {
  if (!val) return null;
  const num = parseFloat(String(val).replace(',', '.').trim());
  return isNaN(num) ? null : num;
};

/* ========================= ICONS ========================= */
const getDotIcon = (color: string, selected: boolean, order?: number) => L.divIcon({
  className: '',
  iconSize: selected ? [22, 22] : [12, 12],
  iconAnchor: selected ? [11, 11] : [6, 6],
  html: selected ? `<div style="position:relative;width:22px;height:22px;display:flex;align-items:center;justify-content:center;">
            <div style="position:absolute;inset:0;border-radius:999px;border:2px solid #22d3ee;
              box-shadow:0 0 14px rgba(34,211,238,0.9);animation:lrPulse 1.8s ease-out infinite;"></div>
            <div style="width:14px;height:14px;border-radius:999px;background:${color};
              border:2px solid #fff;display:flex;align-items:center;justify-content:center;
              font-size:8px;font-weight:700;color:#fff;box-shadow:0 0 10px ${color};">${order ?? ''}</div>
          </div>` : `<div style="width:12px;height:12px;border-radius:999px;background:${color};
            border:2px solid rgba(255,255,255,0.9);box-shadow:0 0 8px ${color};"></div>`
});

/** Terminal Metro node — diamond so it reads as the backbone endpoint. */
const getMetroIcon = () => L.divIcon({
  className: '',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  html: `<div style="position:relative;width:20px;height:20px;display:flex;align-items:center;justify-content:center;">
        <div style="position:absolute;inset:0;transform:rotate(45deg);border-radius:4px;
          border:2px solid #22d3ee;box-shadow:0 0 16px rgba(34,211,238,0.85);
          animation:lrPulse 2.2s ease-out infinite;"></div>
        <div style="width:12px;height:12px;transform:rotate(45deg);border-radius:2px;
          background:#22d3ee;border:2px solid #fff;box-shadow:0 0 12px #22d3ee;"></div>
      </div>`
});

/** Ruler endpoint — amber square so it never reads as a network node. */
const getMeasureIcon = (label: 'A' | 'B') => L.divIcon({
  className: '',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
  html: `<div style="width:18px;height:18px;border-radius:5px;background:#fbbf24;
        border:2px solid #0b1220;display:flex;align-items:center;justify-content:center;
        font-size:9px;font-weight:800;color:#0b1220;box-shadow:0 0 12px rgba(251,191,36,0.85);">${label}</div>`
});
const getHopIcon = (color: string, label: string) => L.divIcon({
  className: '',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
  html: `<div style="width:16px;height:16px;border-radius:999px;background:${color};
        border:2px solid #fff;display:flex;align-items:center;justify-content:center;
        font-size:8px;font-weight:700;color:#08131f;box-shadow:0 0 10px ${color};">${label}</div>`
});

/* ========================= MAP HELPERS ========================= */
function FocusSearchArea({
  centers,
  hasSearch
}: {
  centers: [number, number][];
  hasSearch: boolean;
}) {
  const map = useMap();
  const signature = JSON.stringify(centers);
  useEffect(() => {
    if (!hasSearch || !centers.length) return;

    // Satu hasil: zoom ke site itu. Banyak hasil: rangkum semuanya.
    if (centers.length === 1) {
      map.flyTo(centers[0], 10, {
        duration: 0.8
      });
      return;
    }
    map.fitBounds(centers, {
      padding: [80, 80],
      maxZoom: 11
    });
  }, [signature, hasSearch, map]);
  return null;
}
function FocusRoutes({
  bounds
}: {
  bounds: [number, number][];
}) {
  const map = useMap();
  useEffect(() => {
    if (bounds.length < 2) return;
    map.fitBounds(bounds, {
      padding: [70, 70],
      maxZoom: 13
    });
  }, [JSON.stringify(bounds), map]);
  return null;
}
function ZoomWatcher({
  onZoom
}: {
  onZoom: (z: number) => void;
}) {
  const map = useMapEvents({
    zoomend: () => onZoom(map.getZoom())
  });
  useEffect(() => {
    onZoom(map.getZoom());
  }, []);
  return null;
}

/** Popup action buttons — needs map context to close the popup after acting. */
function PopupActions({
  isSelected,
  measureIndex,
  onToggleTrace,
  onOpenDetail,
  onMeasure
}: {
  isSelected: boolean;
  /** 0 = titik A ruler, 1 = titik B ruler, -1 = belum dipakai. */
  measureIndex: number;
  onToggleTrace: () => void;
  onOpenDetail: () => void;
  onMeasure: () => void;
}) {
  const map = useMap();
  const measuring = measureIndex >= 0;
  return <div className="mt-3 space-y-1.5" data-id="element-687" data-mp="ds4pn72lx2zo">
      <div className="flex gap-1.5" data-id="element-688" data-mp="dr31q20hsm0j">
        <button onClick={() => {
        onToggleTrace();
        map.closePopup();
      }} className={`flex flex-1 items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-colors ${isSelected ? 'bg-red-500/15 text-red-300 border border-red-400/40 hover:bg-red-500/25' : 'bg-cyan-500/15 text-cyan-200 border border-cyan-400/40 hover:bg-cyan-500/25'}`} data-id="element-689" data-mp="isa9i12mxl77">
          {isSelected ? <EyeOffIcon size={12} data-id="element-690" data-mp="4rzhgupla0kg" /> : <EyeIcon size={12} data-id="element-691" data-mp="4shkqbao5f1v" />}
          {isSelected ? 'Sembunyikan jalur' : 'Tampilkan jalur ke Metro'}
        </button>

        {/* Ruler — aksi terpisah dengan ikon sendiri */}
        <button onClick={() => {
        onMeasure();
        map.closePopup();
      }} aria-label={measuring ? 'Hapus titik ukur' : 'Jadikan titik ukur jarak'} title={measuring ? `Titik ukur ${measureIndex === 0 ? 'A' : 'B'} — klik untuk hapus` : 'Ukur jarak dari site ini'} className={`relative flex items-center justify-center w-9 rounded-lg border transition-colors flex-shrink-0 ${measuring ? 'bg-amber-400/20 border-amber-300/60 text-amber-200' : 'bg-slate-700/60 border-slate-600/60 text-slate-300 hover:bg-amber-400/15 hover:text-amber-200 hover:border-amber-300/50'}`} data-id="element-692" data-mp="38h2r1rvuj14p">
          <RulerIcon size={13} data-id="element-693" data-mp="xsxtchoya3qb" />
          {measuring && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-amber-400 text-[8px] font-bold text-slate-950 flex items-center justify-center" data-id="element-694" data-mp="sx8ted90qkth">
              {measureIndex === 0 ? 'A' : 'B'}
            </span>}
        </button>
      </div>

      <button onClick={() => {
      onOpenDetail();
      map.closePopup();
    }} className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-slate-700/60 text-slate-200 border border-slate-600/60 hover:bg-slate-600/60 transition-colors" data-id="element-695" data-mp="27s8jovabnrag">
        Detail lengkap
        <ArrowRightIcon size={12} data-id="element-696" data-mp="22rb32n4yn5b2" />
      </button>
    </div>;
}
interface MapNode {
  id: string;
  lat: number;
  lng: number;
  alproType: string;
  /** 'own' = dari uplink site itu sendiri, 'inferred' = dari link masuk */
  alproSource?: 'own' | 'inferred' | 'none';
}
interface Hop {
  row: any;
  latNE: number;
  lngNE: number;
  latFE: number;
  lngFE: number;
  key: string;
  order: number;
}
interface TracedPath {
  startId: string;
  hops: Hop[];
  reachedMetro: boolean;
  metroNode: string | null;
}
export default function LinkRoute() {
  const [data, setData] = useState<any[]>([]);
  const [regionals, setRegionals] = useState<string[]>([]);
  const [selectedRegional, setSelectedRegional] = useState('');
  const [search, setSearch] = useState('');
  const [zoom, setZoom] = useState(5);
  const [regionalLoading, setRegionalLoading] = useState(true);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeRecordCount, setRouteRecordCount] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);
  // Hop tambahan yang diambil khusus saat menelusuri jalur ke Metro.
  const [traceRows, setTraceRows] = useState<any[]>([]);
  const [traceLoadingIds, setTraceLoadingIds] = useState<string[]>([]);
  const [drawerSite, setDrawerSite] = useState<any>(null);
  const [limitWarning, setLimitWarning] = useState(false);
  // Ruler: maksimal dua titik (A → B) untuk mengukur jarak antar site.
  const [measurePoints, setMeasurePoints] = useState<MapNode[]>([]);
  const [terrain, setTerrain] = useState<TerrainProfileData | null>(null);
  const [terrainLoading, setTerrainLoading] = useState(false);
  const [terrainError, setTerrainError] = useState<string | null>(null);
  const terrainRef = React.useRef<HTMLDivElement | null>(null);
  const [lossCheckOpen, setLossCheckOpen] = useState(false);

  /* ===== FETCH REGIONAL DARI TABEL link_routes ===== */
  useEffect(() => {
    const fetchRegionals = async () => {
      setRegionalLoading(true);
      setLoadError(null);
      const {
        data: regionalRows,
        error
      } = await withGlobalLoading('linkroute-regionals', 'Memuat daftar Regional…', () => supabase.from('link_routes').select('regional').not('regional', 'is', null).order('regional', {
        ascending: true
      }).limit(1000));
      if (error) {
        setLoadError('Gagal memuat daftar Regional.');
        setRegionals([]);
      } else {
        const uniqueRegionals = Array.from(new Set((regionalRows || []).map((row: {
          regional: string | null;
        }) => row.regional?.trim()).filter((regional): regional is string => Boolean(regional))));
        setRegionals(uniqueRegionals);
      }
      setRegionalLoading(false);
    };
    fetchRegionals();
  }, []);

  /* =========================================================
     FETCH DATA — pagination server-side, complete per filter
  ========================================================= */
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      // Tidak ada data peta yang dimuat sebelum user memilih Regional.
      if (!selectedRegional) {
        setData([]);
        setRouteRecordCount(0);
        setRouteLoading(false);
        return;
      }
      setRouteLoading(true);
      setLoadError(null);
      setSelectedSiteIds([]);
      setTraceRows([]);
      setTraceLoadingIds([]);
      setDrawerSite(null);
      const allRows: any[] = [];
      let page = 0;
      while (true) {
        const {
          data: pageRows,
          error
        } = await withGlobalLoading('linkroute-data', `Memuat rute ${selectedRegional}…`, () => supabase.from('link_routes').select('*').eq('regional', selectedRegional).range(page * ROUTE_PAGE_SIZE, (page + 1) * ROUTE_PAGE_SIZE - 1));
        if (cancelled) return;
        if (error) {
          setData([]);
          setRouteRecordCount(0);
          setLoadError(`Gagal memuat rute untuk Regional ${selectedRegional}.`);
          setRouteLoading(false);
          return;
        }
        const rows = pageRows || [];
        allRows.push(...rows);
        if (rows.length < ROUTE_PAGE_SIZE) break;
        page += 1;
      }
      if (!cancelled) {
        setData(allRows);
        // Ini adalah jumlah row route yang benar-benar dipaginasi dari Supabase,
        // bukan jumlah marker/titik unik pada peta.
        setRouteRecordCount(allRows.length);
        setRouteLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [selectedRegional]);

  /* =========================================================
     SEARCH FILTER
      Pencarian di-debounce dan butuh minimal 3 karakter. Tanpa ini,
     keyword pendek seperti "N" cocok dengan ribuan site sekaligus
     sehingga render marker membekukan halaman.
  ========================================================= */
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 280);
    return () => clearTimeout(timer);
  }, [search]);
  const rawTerm = debouncedSearch.trim().toLowerCase();
  const searchTooShort = rawTerm.length > 0 && rawTerm.length < MIN_SEARCH_CHARS;
  const searchTerm = searchTooShort ? '' : rawTerm;

  // Set agar pencocokan node berjalan O(1), bukan O(n) per node.
  const matchingSiteIds = useMemo(() => {
    const matches = new Set<string>();
    if (!searchTerm) return matches;
    data.forEach(row => {
      if (row.site_id_ne?.toLowerCase().includes(searchTerm)) {
        matches.add(row.site_id_ne);
      }
      if (row.site_id_fe?.toLowerCase().includes(searchTerm)) {
        matches.add(row.site_id_fe);
      }
    });
    return matches;
  }, [data, searchTerm]);

  /* =========================================================
     DATA UNTUK TRACE
      `data` berisi rute Regional yang sedang dilihat, sedangkan
     `traceRows` menambahkan hop untuk jalur aktif menuju Metro.
  ========================================================= */
  const routeGraphData = useMemo(() => {
    const seen = new Set<string>();
    return [...data, ...traceRows].filter(row => {
      const key = `${row.site_id_ne}__${row.site_id_fe}__${row.id ?? ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [data, traceRows]);

  /* ===== ADJACENCY (dibangun lebih dulu agar tipe node akurat) ===== */
  const linksByNE = useMemo(() => {
    const map = new Map<string, any[]>();
    routeGraphData.forEach(row => {
      if (!row.site_id_ne) return;
      if (!map.has(row.site_id_ne)) map.set(row.site_id_ne, []);
      map.get(row.site_id_ne)!.push(row);
    });
    return map;
  }, [routeGraphData]);
  const linksByFE = useMemo(() => {
    const map = new Map<string, any[]>();
    routeGraphData.forEach(row => {
      if (!row.site_id_fe) return;
      if (!map.has(row.site_id_fe)) map.set(row.site_id_fe, []);
      map.get(row.site_id_fe)!.push(row);
    });
    return map;
  }, [routeGraphData]);

  /* =========================================================
     ALPRO EXISTING PER SITE
      Warna titik harus mengikuti alpro existing milik SITE itu
     sendiri — yaitu link di mana site tersebut berperan sebagai
     NE (uplink-nya). Sebelumnya warna diambil dari baris pertama
     yang kebetulan ditemukan, sehingga site ber-alpro Metro bisa
     ikut terwarnai oranye karena tertimpa link radio milik
     tetangganya.
  ========================================================= */
  const resolveNodeAlpro = React.useCallback((siteId: string): {
    type: string;
    source: 'own' | 'inferred' | 'none';
  } => {
    const asNE = linksByNE.get(siteId) || [];
    if (asNE.length) {
      // Metro menang: kalau site punya uplink Metro, site itu Metro.
      if (asNE.some(r => isMetro(r.alpro_type))) {
        const metroRow = asNE.find(r => isMetro(r.alpro_type));
        return {
          type: metroRow?.alpro_type || 'Metro',
          source: 'own'
        };
      }
      const radioRow = asNE.find(r => getAlproKind(r.alpro_type) === 'radio');
      return {
        type: radioRow?.alpro_type || asNE[0].alpro_type || '',
        source: 'own'
      };
    }

    // Site tidak pernah menjadi NE (far-end murni).
    // Hanya boleh Metro bila memang ada link Metro yang menuju ke sana.
    const asFE = linksByFE.get(siteId) || [];
    if (asFE.some(r => isMetro(r.alpro_type))) {
      const metroRow = asFE.find(r => isMetro(r.alpro_type));
      return {
        type: metroRow?.alpro_type || 'Metro',
        source: 'inferred'
      };
    }
    return {
      type: '',
      source: 'none'
    };
  }, [linksByNE, linksByFE]);

  /* =========================================================
     NODE INDEX + 50 KM SEARCH CONTEXT
      Peta tidak lagi memplot seluruh Regional. `allNodes`
     menjadi index lokal dari data yang sudah difilter; marker yang
     dirender hanya match pencarian dan site dalam radius 50 km.
  ========================================================= */
  const allNodes = useMemo(() => {
    const coords = new Map<string, {
      lat: number;
      lng: number;
    }>();

    // Pass 1 — koordinat dari sisi NE (paling otoritatif untuk site itu).
    routeGraphData.forEach(row => {
      const lat = parseCoord(row.lat_ne);
      const lng = parseCoord(row.long_ne);
      if (row.site_id_ne && lat !== null && lng !== null && !coords.has(row.site_id_ne)) {
        coords.set(row.site_id_ne, {
          lat,
          lng
        });
      }
    });

    // Pass 2 — lengkapi far-end yang belum punya koordinat.
    routeGraphData.forEach(row => {
      const lat = parseCoord(row.lat_fe);
      const lng = parseCoord(row.long_fe);
      if (row.site_id_fe && lat !== null && lng !== null && !coords.has(row.site_id_fe)) {
        coords.set(row.site_id_fe, {
          lat,
          lng
        });
      }
    });
    return Array.from(coords.entries()).map(([id, c]) => {
      const alpro = resolveNodeAlpro(id);
      return {
        id,
        lat: c.lat,
        lng: c.lng,
        alproType: alpro.type,
        alproSource: alpro.source
      } as MapNode;
    });
  }, [routeGraphData, resolveNodeAlpro]);

  /**
   * Semua site yang cocok dengan keyword menjadi pusat radius, bukan
   * hanya satu. Mencari "TPJ" berarti setiap site TPJ membawa area
   * 50 km-nya sendiri, termasuk tetangga bernama lain seperti TPA.
   */
  const searchCenters = useMemo(() => {
    if (!searchTerm || matchingSiteIds.size === 0) return [] as MapNode[];
    const centers = allNodes.filter(node => matchingSiteIds.has(node.id));

    // Exact match diletakkan pertama supaya peta memfokus ke sana.
    centers.sort((a, b) => {
      const aExact = a.id.toLowerCase() === searchTerm ? 0 : 1;
      const bExact = b.id.toLowerCase() === searchTerm ? 0 : 1;
      return aExact - bExact;
    });
    return centers.length > MAX_SEARCH_CENTERS ? centers.slice(0, MAX_SEARCH_CENTERS) : centers;
  }, [allNodes, matchingSiteIds, searchTerm]);
  const searchCenter = searchCenters[0] ?? null;

  /* =========================================================
     TRACE PATH: NE yang diklik ➝ ikuti alpro existing ➝ Metro
  ========================================================= */
  const tracePathToMetro = React.useCallback((startId: string): TracedPath => {
    const hops: Hop[] = [];
    const visited = new Set<string>([startId]);
    let current = startId;
    let reachedMetro = false;
    let metroNode: string | null = null;
    for (let i = 0; i < MAX_HOPS; i++) {
      const outgoing = linksByNE.get(current) || [];
      if (!outgoing.length) break;
      const metroHop = outgoing.find(r => isMetro(r.alpro_type));
      const row = metroHop ?? [...outgoing].sort((a, b) => getCapacityTier(b).weight - getCapacityTier(a).weight)[0];
      if (!row) break;
      const latNE = parseCoord(row.lat_ne);
      const lngNE = parseCoord(row.long_ne);
      const latFE = parseCoord(row.lat_fe);
      const lngFE = parseCoord(row.long_fe);
      if (latNE === null || lngNE === null || latFE === null || lngFE === null) break;
      hops.push({
        row,
        latNE,
        lngNE,
        latFE,
        lngFE,
        key: `${row.site_id_ne}__${row.site_id_fe}`,
        order: i + 1
      });
      const next = row.site_id_fe;
      if (isMetro(row.alpro_type)) {
        reachedMetro = true;
        metroNode = next;
        break;
      }
      if (!next || visited.has(next)) break;
      visited.add(next);
      current = next;
    }
    return {
      startId,
      hops,
      reachedMetro,
      metroNode
    };
  }, [linksByNE]);
  const tracedPaths = useMemo(() => selectedSiteIds.map(id => tracePathToMetro(id)), [selectedSiteIds, tracePathToMetro]);
  const totalHops = tracedPaths.reduce((s, p) => s + p.hops.length, 0);
  const tracedNodeIds = useMemo(() => {
    const ids = new Set<string>(selectedSiteIds);
    tracedPaths.forEach(path => {
      path.hops.forEach(hop => {
        ids.add(hop.row.site_id_ne);
        ids.add(hop.row.site_id_fe);
      });
    });
    return ids;
  }, [selectedSiteIds, tracedPaths]);
  const {
    visibleNodes,
    nodesCapped
  } = useMemo(() => {
    // Search-first mode: no keyword, no site markers.
    if (!searchTerm || !searchCenters.length) {
      return {
        visibleNodes: [] as MapNode[],
        nodesCapped: false
      };
    }

    // Pra-filter kotak agar sebagian besar node ditolak tanpa haversine.
    const latPad = NEARBY_RADIUS_KM / 111;
    const boxes = searchCenters.map(center => {
      const lngPad = NEARBY_RADIUS_KM / (111 * Math.max(Math.cos(center.lat * Math.PI / 180), 0.1));
      return {
        center,
        minLat: center.lat - latPad,
        maxLat: center.lat + latPad,
        minLng: center.lng - lngPad,
        maxLng: center.lng + lngPad
      };
    });

    /** Jarak ke pusat pencarian terdekat, Infinity bila di luar semua radius. */
    const nearestCenterDistance = (node: MapNode) => {
      let nearest = Infinity;
      for (const box of boxes) {
        if (node.lat < box.minLat || node.lat > box.maxLat || node.lng < box.minLng || node.lng > box.maxLng) {
          continue;
        }
        const d = distanceKm(box.center, node);
        if (d < nearest) nearest = d;
        if (nearest === 0) break;
      }
      return nearest;
    };
    const matched: Array<{
      node: MapNode;
      distance: number;
    }> = [];
    allNodes.forEach(node => {
      if (tracedNodeIds.has(node.id)) {
        matched.push({
          node,
          distance: -2
        });
        return;
      }
      if (matchingSiteIds.has(node.id)) {
        matched.push({
          node,
          distance: -1
        });
        return;
      }
      const distance = nearestCenterDistance(node);
      if (distance <= NEARBY_RADIUS_KM) matched.push({
        node,
        distance
      });
    });
    if (matched.length <= MAX_VISIBLE_NODES) {
      return {
        visibleNodes: matched.map(m => m.node),
        nodesCapped: false
      };
    }

    // Jalur aktif dan hasil pencarian menang, sisanya diambil yang terdekat.
    matched.sort((a, b) => a.distance - b.distance);
    return {
      visibleNodes: matched.slice(0, MAX_VISIBLE_NODES).map(m => m.node),
      nodesCapped: true
    };
  }, [allNodes, matchingSiteIds, searchCenters, searchTerm, tracedNodeIds]);
  const focusBounds = useMemo(() => {
    const b: [number, number][] = [];
    tracedPaths.forEach(p => p.hops.forEach(h => {
      b.push([h.latNE, h.lngNE]);
      b.push([h.latFE, h.lngFE]);
    }));
    return b;
  }, [tracedPaths]);
  const metroEndpoints = useMemo(() => {
    const map = new Map<string, [number, number]>();
    tracedPaths.forEach(p => {
      if (!p.reachedMetro || !p.hops.length) return;
      const last = p.hops[p.hops.length - 1];
      map.set(p.metroNode || last.row.site_id_fe, [last.latFE, last.lngFE]);
    });
    return Array.from(map.entries());
  }, [tracedPaths]);

  /* =========================================================
     AMBIL HOP TRACE MENUJU METRO
      Query dibatasi Regional dan hanya mengambil hop pada jalur yang
     diklik, sehingga peta tetap ringan sampai Metro ditemukan.
  ========================================================= */
  const loadTraceRowsToMetro = React.useCallback(async (startId: string) => {
    if (!selectedRegional || traceLoadingIds.includes(startId)) return;
    setTraceLoadingIds(prev => [...prev, startId]);
    const collected: any[] = [];
    const visited = new Set<string>([startId]);
    let current = startId;
    try {
      for (let hop = 0; hop < MAX_HOPS; hop += 1) {
        const {
          data: outgoingRows,
          error
        } = await withGlobalLoading('linkroute-trace', `Menelusuri jalur ${startId} ke Metro…`, () => supabase.from('link_routes').select('*').eq('regional', selectedRegional).eq('site_id_ne', current).limit(100));
        if (error || !outgoingRows?.length) break;
        const metroHop = outgoingRows.find(row => isMetro(row.alpro_type));
        const chosen = metroHop ?? [...outgoingRows].sort((a, b) => getCapacityTier(b).weight - getCapacityTier(a).weight)[0];
        if (!chosen) break;
        collected.push(chosen);
        if (isMetro(chosen.alpro_type) || !chosen.site_id_fe) break;
        if (visited.has(chosen.site_id_fe)) break;
        visited.add(chosen.site_id_fe);
        current = chosen.site_id_fe;
      }
      setTraceRows(previous => {
        const existing = new Set(previous.map(row => `${row.site_id_ne}__${row.site_id_fe}__${row.id ?? ''}`));
        return [...previous, ...collected.filter(row => {
          const key = `${row.site_id_ne}__${row.site_id_fe}__${row.id ?? ''}`;
          if (existing.has(key)) return false;
          existing.add(key);
          return true;
        })];
      });
    } finally {
      setTraceLoadingIds(prev => prev.filter(id => id !== startId));
    }
  }, [selectedRegional, traceLoadingIds]);

  /* ===== ACTIONS ===== */
  const showTraceToMetro = (siteId: string) => {
    setLimitWarning(false);
    if (selectedSiteIds.includes(siteId)) return;
    if (selectedSiteIds.length >= MAX_SELECTION) {
      setLimitWarning(true);
      return;
    }
    setSelectedSiteIds(prev => [...prev, siteId]);
    void loadTraceRowsToMetro(siteId);
  };
  const toggleMeasurePoint = (node: MapNode) => {
    setMeasurePoints(prev => {
      const existing = prev.findIndex(p => p.id === node.id);
      if (existing >= 0) return prev.filter(p => p.id !== node.id);
      // Titik ketiga memulai pengukuran baru dari titik terakhir.
      if (prev.length >= 2) return [prev[1], node];
      return [...prev, node];
    });
  };
  const measureDistance = measurePoints.length === 2 ? distanceKm(measurePoints[0], measurePoints[1]) : null;

  /* =========================================================
     PROFIL DATARAN
      Ketinggian diambil dari Open-Meteo Elevation API (Copernicus
     DEM 90 m) hanya ketika kedua titik ruler sudah dipilih.
  ========================================================= */
  useEffect(() => {
    let cancelled = false;
    if (measurePoints.length < 2) {
      setTerrain(null);
      setTerrainError(null);
      setTerrainLoading(false);
      return;
    }
    const [from, to] = measurePoints;
    setTerrainLoading(true);
    setTerrainError(null);

    // Panel bisa berada di luar viewport saat peta sedang di-scroll.
    requestAnimationFrame(() => terrainRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    }));
    withGlobalLoading('terrain-profile', 'Memuat profil dataran…', () => fetchTerrainProfile(from, to)).then(result => {
      if (!cancelled) setTerrain(result);
    }).catch((err: Error) => {
      if (!cancelled) {
        setTerrain(null);
        setTerrainError(err.message);
      }
    }).finally(() => {
      if (!cancelled) setTerrainLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [measurePoints.length, measurePoints[0]?.id, measurePoints[1]?.id]);
  const toggleTrace = (siteId: string) => {
    if (selectedSiteIds.includes(siteId)) {
      setSelectedSiteIds(prev => prev.filter(id => id !== siteId));
      return;
    }
    showTraceToMetro(siteId);
  };
  const openDetail = async (siteId: string) => {
    const asNE = linksByNE.get(siteId) || [];
    const loadedParents = linksByFE.get(siteId) || [];

    // Ambil semua anakan ketika site menjadi FE.
    // Query kecil dan spesifik ini menjaga peta ringan tetapi relasi node lengkap.
    let incomingLinks = loadedParents;
    if (selectedRegional) {
      const {
        data: incomingRows
      } = await withGlobalLoading('linkroute-detail', `Memuat detail ${siteId}…`, () => supabase.from('link_routes').select('*').eq('regional', selectedRegional).eq('site_id_fe', siteId).limit(200));
      if (incomingRows) {
        incomingLinks = incomingRows;
        setTraceRows(previous => {
          const existing = new Set(previous.map(row => `${row.site_id_ne}__${row.site_id_fe}__${row.id ?? ''}`));
          return [...previous, ...incomingRows.filter(row => {
            const key = `${row.site_id_ne}__${row.site_id_fe}__${row.id ?? ''}`;
            if (existing.has(key)) return false;
            existing.add(key);
            return true;
          })];
        });
      }
    }
    const primary = asNE[0] || incomingLinks[0] || {};
    setDrawerSite({
      ...primary,
      site_id: siteId,
      children: asNE,
      parents: incomingLinks,
      path: tracePathToMetro(siteId),
      upstream: [],
      upstreamLoading: Boolean(selectedRegional),
      upstreamTruncated: false
    });
    if (!selectedRegional) return;

    // Telusuri seluruh rantai di hulu — NE yang mengarah ke site ini, lalu
    // NE di atasnya lagi, sampai ujung radio.
    const {
      children,
      rows,
      truncated
    } = await withGlobalLoading('linkroute-upstream', `Menelusuri rantai radio ${siteId}…`, () => fetchUpstreamTree(selectedRegional, siteId));
    setTraceRows(previous => {
      const existing = new Set(previous.map(row => `${row.site_id_ne}__${row.site_id_fe}__${row.id ?? ''}`));
      return [...previous, ...rows.filter(row => {
        const key = `${row.site_id_ne}__${row.site_id_fe}__${row.id ?? ''}`;
        if (existing.has(key)) return false;
        existing.add(key);
        return true;
      })];
    });
    setDrawerSite((current: any) => current?.site_id === siteId ? {
      ...current,
      upstream: children,
      upstreamLoading: false,
      upstreamTruncated: truncated
    } : current);
  };
  useEffect(() => {
    if (!limitWarning) return;
    const t = setTimeout(() => setLimitWarning(false), 3200);
    return () => clearTimeout(t);
  }, [limitWarning]);
  const showLabels = zoom >= LABEL_MIN_ZOOM;
  const defaultCenter: [number, number] = [-2.5, 118];
  return <div className="space-y-5" data-id="element-697" data-mp="q5k91udtjunn">
      {/* HEADER */}
      <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-698" data-mp="q756psvfjtpq">
        <div data-id="element-699" data-mp="1mu03a3rvis73">
          <h2 className="text-xl font-bold text-white tracking-tight" data-id="element-700" data-mp="1mujktjdm3ekl">
            Link Route Topology
          </h2>
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5" data-id="element-701" data-mp="1hykptcxs2yb7">
            <MousePointerClickIcon size={13} className="text-cyan-400" data-id="element-702" data-mp="1d3oy6dwgecym" />
            Klik titik site untuk melihat info & aksi · jalur ditarik mengikuti
            alpro existing sampai Metro · maksimal {MAX_SELECTION} site
          </p>
        </div>

        <div className="flex items-end gap-3 flex-wrap" data-id="element-703" data-mp="20ukjqtabgbxf">
          <div className="flex flex-col gap-1.5" data-id="element-704" data-mp="20szrjabh552q">
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.12em]" data-id="element-705" data-mp="3jim5plr248ql">
              Regional
            </label>
            <select value={selectedRegional} onChange={e => setSelectedRegional(e.target.value)} disabled={regionalLoading} className="filter-select text-sm rounded-lg px-3 py-2 border border-slate-700/70 text-slate-200 outline-none hover:border-slate-600 transition-colors disabled:opacity-60 disabled:cursor-wait min-w-[180px]" data-id="element-706" data-mp="3oj1ee00e8s28">
              <option value="" className="bg-slate-900" data-id="element-707" data-mp="b8apmgglxb9c">
                {regionalLoading ? 'Memuat Regional…' : 'Pilih Regional'}
              </option>
              {regionals.map(r => <option key={r} value={r} className="bg-slate-900" data-id="element-708" data-mp="k4tijsou8z5x">
                  {r}
                </option>)}
            </select>
          </div>

          <button onClick={() => setLossCheckOpen(v => !v)} aria-pressed={lossCheckOpen} className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold border transition-colors ${lossCheckOpen ? 'bg-cyan-500/15 border-cyan-400/40 text-cyan-200' : 'bg-slate-800/70 border-slate-700/70 text-slate-300 hover:text-cyan-200 hover:border-cyan-400/40'}`} data-id="element-709" data-mp="u1y4brerusm7">
            <SignalIcon size={14} data-id="element-710" data-mp="3tk6auiavlqi7" />
            Cek Loss
          </button>

          <div className="flex flex-col gap-1.5" data-id="element-711" data-mp="3olmnqekjq8o9">
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.12em]" data-id="element-712" data-mp="3omwml5z83kvy">
              Search
            </label>
            <div className="relative w-full md:w-72" data-id="element-713" data-mp="7b54ag843rlk">
              <SearchIcon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" data-id="element-714" data-mp="2dlpnf7i5ct3" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Site ID NE / FE (min ${MIN_SEARCH_CHARS} huruf)…`} className="input-dark w-full pl-9 pr-9 text-sm py-2" data-id="element-715" data-mp="1du3asj5nft0f" />
              {search !== debouncedSearch && <span className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" data-id="element-716" data-mp="1cdpqydvkxs7i" />}
            </div>
          </div>
        </div>
      </div>

      {/* SELECTION BAR */}
      <div className="glass-panel rounded-2xl p-4 flex flex-wrap items-center gap-3" data-id="element-717" data-mp="292dts2yq58wc">
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-718" data-mp="1vb8rsu7vw4iu">
          <div className="p-1.5 rounded-lg bg-cyan-500/10 border border-cyan-400/30 text-cyan-300" data-id="element-719" data-mp="1vb0fp4jl538p">
            <RouteIcon size={15} data-id="element-720" data-mp="1zb167njyl4w4" />
          </div>
          <div className="flex flex-col leading-tight" data-id="element-721" data-mp="2n1wr00pzyztv">
            <span className="text-xs font-semibold text-slate-200" data-id="element-722" data-mp="2n0bwuc7lhiwj">
              Traced to Metro
            </span>
            <span className="text-[10px] font-mono text-slate-500" data-id="element-723" data-mp="31p58fj9wg1ea">
              {selectedSiteIds.length}/{MAX_SELECTION} site · {totalHops} hop
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 flex-1 min-w-0" data-id="element-724" data-mp="2xlpgh08ryp7x">
          <AnimatePresence mode="popLayout" data-id="element-725" data-mp="2smvz2wqdae2h">
            {tracedPaths.map((p, idx) => <motion.button key={p.startId} layout initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            duration: 0.18
          }} onClick={() => setSelectedSiteIds(prev => prev.filter(s => s !== p.startId))} title={p.reachedMetro ? `Terhubung ke Metro: ${p.metroNode}` : 'Metro tidak ditemukan pada jalur ini'} className={`group flex items-center gap-1.5 pl-2 pr-1.5 py-1 rounded-lg border text-[11px] font-mono transition-colors ${p.reachedMetro ? 'bg-cyan-500/10 border-cyan-400/30 text-cyan-200 hover:bg-red-500/15 hover:border-red-400/40 hover:text-red-200' : 'bg-amber-500/10 border-amber-400/30 text-amber-200 hover:bg-red-500/15 hover:border-red-400/40 hover:text-red-200'}`} data-id="element-726" data-mp="2nokogjs8zzj7" data-mpi={idx}>
                <span className="w-4 h-4 rounded bg-white/10 flex items-center justify-center text-[9px] font-bold" data-id="element-727" data-mp="1245yrkbrnsd4">
                  {idx + 1}
                </span>
                <span className="truncate max-w-[130px]" data-id="element-728" data-mp="1c3oflrwdqy0y">{p.startId}</span>
                <span className="opacity-70" data-id="element-729" data-mp="1gvesa4ishxvj">·{p.hops.length}h</span>
                <XIcon size={11} className="opacity-60 group-hover:opacity-100" data-id="element-730" data-mp="jfskusqdfg6t" />
              </motion.button>)}
          </AnimatePresence>

          {selectedSiteIds.length === 0 && <span className="text-[11px] text-slate-600 italic self-center" data-id="element-731" data-mp="22lc6j4mizilz">
              Belum ada site dipilih — peta menampilkan titik saja.
            </span>}
        </div>

        {/* RULER READOUT */}
        {measurePoints.length > 0 && <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg bg-slate-800/70 border border-slate-600/50 flex-shrink-0" data-id="element-732" data-mp="2cl2yoznm5liw">
            <RulerIcon size={14} className="text-slate-300 flex-shrink-0" data-id="element-733" data-mp="2lk6k4aa96d0k" />
            <div className="flex flex-col leading-tight min-w-0" data-id="element-734" data-mp="2lid8shop34m8">
              <span className="text-[11px] font-mono text-slate-100 truncate max-w-[240px]" data-id="element-735" data-mp="2hhjqc3u0ui0q">
                {measurePoints[0].id}
                {measurePoints[1] ? ` → ${measurePoints[1].id}` : ' → pilih titik B'}
              </span>
              <span className="text-[10px] font-mono text-slate-400" data-id="element-736" data-mp="2qczo2vxwdhbl">
                {measureDistance === null ? 'Pilih titik B untuk melihat profil dataran' : terrainLoading ? `${measureDistance.toFixed(2)} km · memuat dataran…` : terrain ? `${measureDistance.toFixed(2)} km · puncak ${Math.round(terrain.maxElevation.elevation)} m` : `${measureDistance.toFixed(2)} km`}
              </span>
            </div>
            {measurePoints.length === 2 && <button onClick={() => setLossCheckOpen(true)} className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-cyan-500/15 border border-cyan-400/40 text-cyan-200 text-[10px] font-semibold hover:bg-cyan-500/25 transition-colors flex-shrink-0" data-id="element-737" data-mp="3eu87w8lp5a11">
                <SignalIcon size={11} data-id="element-738" data-mp="2r7rv2smkjil2" />
                Cek loss
              </button>}

            <button onClick={() => setMeasurePoints([])} aria-label="Reset pengukuran" className="p-1 rounded-md text-slate-400 hover:text-red-300 hover:bg-red-500/15 transition-colors flex-shrink-0" data-id="element-739" data-mp="2w5joxs6rxerw">
              <XIcon size={12} data-id="element-740" data-mp="3d7t6qejv42hk" />
            </button>
          </div>}

        {selectedSiteIds.length > 0 && <button onClick={() => setSelectedSiteIds([])} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800/70 border border-slate-700/70 text-slate-300 text-[11px] font-semibold hover:bg-red-500/15 hover:text-red-300 hover:border-red-400/40 transition-all flex-shrink-0" data-id="element-741" data-mp="3i64dggev1wvk">
            <XIcon size={12} data-id="element-742" data-mp="2ujxw33jjr4so" />
            Clear all
          </button>}
      </div>

      {/* LIMIT WARNING */}
      <AnimatePresence data-id="element-743" data-mp="2kjox5hy6759l">
        {limitWarning && <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} role="alert" className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs" data-id="element-744" data-mp="2kkyuu6142bfp">
            <AlertTriangleIcon size={14} className="flex-shrink-0" data-id="element-745" data-mp="npaw435o4iox" />
            <span data-id="element-746" data-mp="28a7odztpcway">
              Maksimal {MAX_SELECTION} site dapat ditampilkan bersamaan. Hapus
              salah satu pilihan untuk menambah site baru.
            </span>
          </motion.div>}
      </AnimatePresence>

      {/* LOSS CHECK — input koordinat manual */}
      {lossCheckOpen && <LossCheckPanel key={`${measurePoints[0]?.id ?? ''}-${measurePoints[1]?.id ?? ''}`} onClose={() => setLossCheckOpen(false)} initialNE={measurePoints[0]} initialFE={measurePoints[1]} data-id="element-747" data-mp="2d6y3ajtrtlus" />}

      {/* TERRAIN PROFILE — tampil tepat di atas peta saat ruler dipakai */}
      {measurePoints.length === 2 && <div ref={terrainRef} data-id="element-748" data-mp="2vyquq9t6yz0e">
          <TerrainProfile fromId={measurePoints[0].id} toId={measurePoints[1].id} profile={terrain} loading={terrainLoading} error={terrainError} onClose={() => setMeasurePoints([])} data-id="element-749" data-mp="2vzjrqj2cma5c" />
        </div>}

      {/* MAP + LEGEND */}
      <div className="glass-panel p-4 rounded-2xl relative" data-id="element-750" data-mp="31pbnfomfamvs">
        <MapContainer center={defaultCenter} zoom={5} className="h-[620px] w-full rounded-xl z-0" data-id="element-751" data-mp="36nd648fxsk6q">
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-752" data-mp="3aq1fqich5v4r" />

          <ZoomWatcher onZoom={setZoom} data-id="element-753" data-mp="3fol1oir2j6x4" />
          <FocusSearchArea centers={searchCenters.map(c => [c.lat, c.lng]) as [number, number][]} hasSearch={Boolean(searchTerm)} data-id="element-754" data-mp="3km4jttqw03uc" />
          <FocusRoutes bounds={focusBounds} data-id="element-755" data-mp="3ubwcujjc8owp" />

          {/* ===== TRACED PATHS ➝ METRO ===== */}
          {tracedPaths.map(path => path.hops.map(hop => {
          const tier = getCapacityTier(hop.row);
          const lastHop = hop.order === path.hops.length;
          return <Fragment key={`hop-${path.startId}-${hop.key}`} data-id="element-756" data-mp="c5rt6kd5d89t">
                  <Polyline positions={[[hop.latNE, hop.lngNE], [hop.latFE, hop.lngFE]]} pathOptions={{
              color: tier.color,
              weight: tier.weight + 6,
              opacity: 0.15
            }} interactive={false} data-id="element-757" data-mp="c46v4krmj777" />
                  <Polyline positions={[[hop.latNE, hop.lngNE], [hop.latFE, hop.lngFE]]} pathOptions={{
              color: tier.color,
              weight: tier.weight,
              opacity: 0.95,
              dashArray: isMetro(hop.row.alpro_type) ? undefined : '10 6'
            }} data-id="element-758" data-mp="150puqyd1kkph">
                    <Tooltip sticky className="lr-tooltip" data-id="element-759" data-mp="1j0qxkjcva27l">
                      <div className="text-[11px] leading-relaxed" data-id="element-760" data-mp="1iz7e6jvwzb1l">
                        <div className="font-semibold text-cyan-300 font-mono" data-id="element-761" data-mp="13hfzsv5egiju">
                          {hop.row.site_id_ne} → {hop.row.site_id_fe}
                        </div>
                        <div className="text-slate-400" data-id="element-762" data-mp="uib2fs5kqv99">
                          {getAlproLabel(hop.row.alpro_type)} ·{' '}
                          <span style={{
                      color: tier.color
                    }} data-id="element-763" data-mp="yk5zulkdwohr">
                            {getCapacityLabel(hop.row)}
                          </span>
                        </div>
                      </div>
                    </Tooltip>
                  </Polyline>

                  {!lastHop && <Marker position={[hop.latFE, hop.lngFE]} icon={getHopIcon(tier.color, String(hop.order))} zIndexOffset={1500} interactive={false} data-id="element-764" data-mp="1dfvlgilnqzly" />}
                </Fragment>;
        }))}

          {/* ===== METRO ENDPOINTS ===== */}
          {metroEndpoints.map(([id, pos]) => <Marker key={`metro-${id}`} position={pos} icon={getMetroIcon()} zIndexOffset={2500} data-id="element-765" data-mp="3ppvj5ydbetm7">
              <Tooltip permanent direction="right" offset={[12, 0]} className="lr-label lr-label-metro" data-id="element-766" data-mp="88m2s5psoyf6">
                {id}
              </Tooltip>
            </Marker>)}

          {/* ===== RULER ===== */}
          {measurePoints.map((point, idx) => <Marker key={`measure-${point.id}`} position={[point.lat, point.lng]} icon={getMeasureIcon(idx === 0 ? 'A' : 'B')} zIndexOffset={3000} interactive={false} data-id="element-767" data-mp="3lo0fwobtq65s" data-mpi={idx} />)}

          {measurePoints.length === 2 && <Polyline positions={measurePoints.map(p => [p.lat, p.lng]) as [number, number][]} pathOptions={{
          color: '#fbbf24',
          weight: 2,
          opacity: 0.95,
          dashArray: '6 6'
        }} interactive={false} data-id="element-768" data-mp="vzhteziilnku">
              <Tooltip permanent direction="center" className="lr-measure" data-id="element-769" data-mp="1hz5jo5inn0b0">
                {measureDistance?.toFixed(2)} km
              </Tooltip>
            </Polyline>}

          {/* ===== SITE DOTS ===== */}
          {visibleNodes.map(node => {
          const selectedIdx = selectedSiteIds.indexOf(node.id);
          const isSelected = selectedIdx >= 0;
          const kind = getAlproKind(node.alproType);
          const labelled = isSelected || showLabels && (kind === 'metro' || kind === 'radio');
          const asNE = linksByNE.get(node.id) || [];
          const asFE = linksByFE.get(node.id) || [];
          const primary = asNE[0] || asFE[0];
          const tier = primary ? getCapacityTier(primary) : UNKNOWN_TIER;
          return <Marker key={`node-${node.id}`} position={[node.lat, node.lng]} icon={getDotIcon(getAlproColor(node.alproType), isSelected, selectedIdx + 1)} zIndexOffset={isSelected ? 2000 : 1000} data-id="element-770" data-mp="2fsezr7jshbf8">
                {labelled && <Tooltip permanent direction="right" offset={[10, 0]} className={`lr-label ${kind === 'metro' ? 'lr-label-metro' : 'lr-label-radio'}`} data-id="element-771" data-mp="1w4z2903xk4hi">
                    {node.id}
                  </Tooltip>}

                <Popup minWidth={230} maxWidth={260} data-id="element-772" data-mp="1i7ajbxtfb7f3">
                  <div className="text-slate-200" data-id="element-773" data-mp="1d5tnigpsqb5d">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-2 mb-2" data-id="element-774" data-mp="31zr7sh7ao3cq">
                      <div className="min-w-0" data-id="element-775" data-mp="361m57am3twl8">
                        <div className="text-[9px] text-slate-500 uppercase tracking-[0.14em] font-semibold" data-id="element-776" data-mp="361cdnilevhdf">
                          Site ID NE
                        </div>
                        <div className="text-sm font-bold text-cyan-300 font-mono truncate" data-id="element-777" data-mp="2x0egnk9e4a4c">
                          {node.id}
                        </div>
                      </div>
                      <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold flex-shrink-0" style={{
                    color: getAlproColor(node.alproType),
                    background: `${getAlproColor(node.alproType)}22`,
                    border: `1px solid ${getAlproColor(node.alproType)}55`
                  }} data-id="element-778" data-mp="2n3jnlgvvz1xg">
                        {getAlproLabel(node.alproType)}
                      </span>
                    </div>

                    {/* Info rows */}
                    <div className="space-y-1 pt-2 border-t border-slate-700/60" data-id="element-779" data-mp="1gp53ba4vpok2">
                      <div className="flex items-center gap-1.5 text-[11px]" data-id="element-780" data-mp="1grhso8cyv175">
                        <span className="w-2 h-2 rounded-full flex-shrink-0 ml-[2px] mr-[1px]" style={{
                      background: getAlproColor(node.alproType),
                      boxShadow: `0 0 6px ${getAlproColor(node.alproType)}`
                    }} data-id="element-781" data-mp="1uq7prnzsd0vi" />
                        <span className="text-slate-500" data-id="element-782" data-mp="2dfns8gv173ek">Alpro existing</span>
                        <span className="ml-auto font-semibold" style={{
                      color: getAlproColor(node.alproType)
                    }} data-id="element-783" data-mp="28gkyhski4jlw">
                          {getAlproLabel(node.alproType)}
                          {node.alproSource === 'inferred' && <span className="text-slate-500 font-normal" data-id="element-784" data-mp="3jtfln8vcxiu0">
                              {' '}
                              (far-end)
                            </span>}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5 text-[11px]" data-id="element-785" data-mp="35ten402kenhp">
                        <GaugeIcon size={11} className="text-slate-500" data-id="element-786" data-mp="35vioobczraet" />
                        <span className="text-slate-500" data-id="element-787" data-mp="30v38zd58lqto">Grouping BW</span>
                        <span className="ml-auto font-mono font-semibold" style={{
                      color: tier.color
                    }} data-id="element-788" data-mp="2w3cviyb06my1">
                          {primary ? getCapacityLabel(primary) : '—'}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5 text-[11px]" data-id="element-789" data-mp="2i2sea6kqkz6n">
                        <GitBranchIcon size={11} className="text-slate-500" data-id="element-790" data-mp="2d47eg89echiq" />
                        <span className="text-slate-500" data-id="element-791" data-mp="smopbm1uoh57">Anakan (as NE)</span>
                        <span className="ml-auto font-mono font-semibold text-slate-200" data-id="element-792" data-mp="sosqhw8d721s">
                          {asNE.length}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5 text-[11px]" data-id="element-793" data-mp="12lnnubswiweh">
                        <TagIcon size={11} className="text-slate-500" data-id="element-794" data-mp="16l6bhfc3xll0" />
                        <span className="text-slate-500" data-id="element-795" data-mp="9wqhn71ysq23">Uplink (as FE)</span>
                        <span className="ml-auto font-mono font-semibold text-slate-200" data-id="element-796" data-mp="evbaumisu9h1">
                          {asFE.length}
                        </span>
                      </div>

                      {isSelected && <div className="flex items-center gap-1.5 text-[11px] pt-1" data-id="element-797" data-mp="nnv9hmz3ce1v">
                          <RouteIcon size={11} className="text-cyan-400" data-id="element-798" data-mp="3nbmw6mduz7hm" />
                          <span className="text-cyan-300 font-semibold" data-id="element-799" data-mp="3rcow75flnnkn">
                            Jalur sedang ditampilkan
                          </span>
                        </div>}
                    </div>

                    <PopupActions isSelected={isSelected} measureIndex={measurePoints.findIndex(p => p.id === node.id)} onToggleTrace={() => toggleTrace(node.id)} onOpenDetail={() => openDetail(node.id)} onMeasure={() => toggleMeasurePoint(node)} data-id="element-800" data-mp="tjwunm5fco33" />
                  </div>
                </Popup>
              </Marker>;
        })}
        </MapContainer>

        {/* State overlays stay outside MapContainer: Leaflet only receives map layers. */}
        {!selectedRegional && !regionalLoading && <div className="absolute inset-4 z-[450] flex items-center justify-center bg-slate-950/55 backdrop-blur-[2px] rounded-xl" data-id="element-801" data-mp="1zyfqul8kw9x0">
            <div className="max-w-xs text-center px-6 py-5 rounded-2xl bg-slate-950/90 border border-cyan-400/20 shadow-glow" data-id="element-802" data-mp="1vx2io4i0jumx">
              <LayersIcon size={22} className="text-cyan-400 mx-auto mb-3" data-id="element-803" data-mp="23jhhxf6z2z8r" />
              <p className="text-sm font-semibold text-slate-200" data-id="element-804" data-mp="28hj1004eeyk8">Pilih Regional</p>
              <p className="text-xs text-slate-500 mt-1.5 leading-relaxed" data-id="element-805" data-mp="1tk8qaszkxwpk">
                Pilih Regional, kemudian cari Site ID untuk memuat area peta.
              </p>
            </div>
          </div>}

        {selectedRegional && !routeLoading && !searchTerm && <div className="absolute inset-4 z-[450] flex items-center justify-center bg-slate-950/45 backdrop-blur-[1px] rounded-xl pointer-events-none" data-id="element-806" data-mp="2r6z4mwbdr74v">
            <div className="max-w-xs text-center px-6 py-5 rounded-2xl bg-slate-950/90 border border-cyan-400/20 shadow-glow" data-id="element-807" data-mp="2cfyvsq6q1b87">
              <SearchIcon size={22} className="text-cyan-400 mx-auto mb-3" data-id="element-808" data-mp="355ysi1r2jhzz" />
              <p className="text-sm font-semibold text-slate-200" data-id="element-809" data-mp="354nrg9p0cock">
                {searchTooShort ? 'Lanjutkan mengetik' : 'Cari Site ID NE'}
              </p>
              <p className="text-xs text-slate-500 mt-1.5 leading-relaxed" data-id="element-810" data-mp="1magloltborw4">
                {searchTooShort ? `Minimal ${MIN_SEARCH_CHARS} karakter, misalnya NTI. Keyword lebih pendek cocok dengan terlalu banyak site.` : `Ketik Site ID, misalnya NTI033. Peta akan memunculkan site tersebut dan jaringan dalam radius ${NEARBY_RADIUS_KM} km.`}
              </p>
            </div>
          </div>}

        {selectedRegional && !routeLoading && searchTerm && !searchCenter && <div className="absolute inset-4 z-[450] flex items-center justify-center bg-slate-950/45 backdrop-blur-[1px] rounded-xl pointer-events-none" data-id="element-811" data-mp="202db2i3lwnot">
            <div className="max-w-xs text-center px-6 py-5 rounded-2xl bg-slate-950/90 border border-amber-400/20" data-id="element-812" data-mp="1qb8vbg63wysx">
              <SearchIcon size={22} className="text-amber-400 mx-auto mb-3" data-id="element-813" data-mp="2nqmpv02ekb6g" />
              <p className="text-sm font-semibold text-slate-200" data-id="element-814" data-mp="2iywb8hwfn198">Site tidak ditemukan</p>
              <p className="text-xs text-slate-500 mt-1.5 leading-relaxed" data-id="element-815" data-mp="2r8aeymw1otba">
                Coba gunakan Site ID NE yang tersedia pada Regional aktif.
              </p>
            </div>
          </div>}

        {nodesCapped && !routeLoading && <div className="absolute bottom-7 left-7 z-[450] px-3 py-2 rounded-lg bg-slate-950/90 border border-amber-400/25 text-[10px] text-amber-200 font-mono pointer-events-none" data-id="element-816" data-mp="30ajqng31usq9">
            Menampilkan {MAX_VISIBLE_NODES} site terdekat — persempit pencarian
          </div>}

        {routeLoading && <div className="absolute inset-4 z-[450] flex items-center justify-center bg-slate-950/45 backdrop-blur-[1px] rounded-xl" data-id="element-817" data-mp="3e5vnbt0rmt9t">
            <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-slate-950/90 border border-cyan-400/20 text-xs text-cyan-200 font-mono" data-id="element-818" data-mp="3nyb3cksi57s2">
              <span className="w-3.5 h-3.5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" data-id="element-819" data-mp="3sv1k7ac4s7ej" />
              Memuat rute {selectedRegional}…
            </div>
          </div>}

        {traceLoadingIds.length > 0 && !routeLoading && <div className="absolute top-7 left-1/2 -translate-x-1/2 z-[450] flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-950/90 border border-cyan-400/25 text-[11px] text-cyan-200 font-mono shadow-glow pointer-events-none" data-id="element-820" data-mp="33abg95ogjhxf">
            <span className="w-3 h-3 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" data-id="element-821" data-mp="3d5btg95jro73" />
            Menelusuri jalur ke Metro…
          </div>}

        {loadError && selectedRegional && !routeLoading && <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[450] max-w-sm text-center px-5 py-4 rounded-xl bg-red-950/90 border border-red-500/30 text-xs text-red-200 shadow-xl" data-id="element-822" data-mp="4rmql3yx2q3i">
            {loadError}
          </div>}

        {/* LEGEND */}
        <div className="absolute bottom-7 right-7 z-[400] glass-panel rounded-xl p-3.5 w-52 pointer-events-none" data-id="element-823" data-mp="21jwmjxhsa7mb">
          <div className="flex items-center gap-2 mb-3" data-id="element-824" data-mp="h12oyy5ukbbc">
            <LayersIcon size={13} className="text-cyan-400" data-id="element-825" data-mp="h3p35gn8tp2j" />
            <span className="text-[10px] font-semibold text-slate-300 uppercase tracking-[0.12em]" data-id="element-826" data-mp="lzdvp00ui5pc">
              Grouping BW
            </span>
          </div>

          <div className="space-y-1.5 mb-3" data-id="element-827" data-mp="cam8142ti7k8">
            {CAPACITY_TIERS.map(tier => <div key={tier.id} className="flex items-center gap-2" data-id="element-828" data-mp="3ugihxk86mz05">
                <span className="rounded-full flex-shrink-0" style={{
              height: `${Math.max(2, tier.weight - 1.4)}px`,
              width: '24px',
              background: tier.color,
              boxShadow: `0 0 6px ${tier.color}`
            }} data-id="element-829" data-mp="3uf7czh303l7g" />
                <span className="text-[10px] text-slate-400 font-mono" data-id="element-830" data-mp="1iqg780rlex47">
                  {tier.label}
                </span>
              </div>)}
            <div className="flex items-center gap-2" data-id="element-831" data-mp="1nnyezntvj6am">
              <span className="h-[3px] w-6 rounded-full flex-shrink-0" style={{
              background: UNKNOWN_TIER.color
            }} data-id="element-832" data-mp="ai4vyvqi16li" />
              <span className="text-[10px] text-slate-500 font-mono" data-id="element-833" data-mp="1qw7qdy5r80r">
                {UNKNOWN_TIER.label}
              </span>
            </div>
          </div>

          <div className="pt-2.5 border-t border-slate-800/70 space-y-1.5" data-id="element-834" data-mp="3swnur4gn24jc">
            <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.12em] mb-1.5" data-id="element-835" data-mp="3nz5jv8j29f8p">
              Node Type
            </div>
            <div className="flex items-center gap-2" data-id="element-836" data-mp="3j1ch6pgyv3d6">
              <span className="w-2.5 h-2.5 rounded-full bg-[#22d3ee] border border-white/80 flex-shrink-0" data-id="element-837" data-mp="3eyfvgpw4qr50" />
              <span className="text-[10px] text-slate-400" data-id="element-838" data-mp="39zmmnarsfob6">Metro</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-839" data-mp="1d4zylb9g8g1l">
              <span className="w-2.5 h-2.5 rounded-full bg-[#f97316] border border-white/80 flex-shrink-0" data-id="element-840" data-mp="186yhuwzhwsta" />
              <span className="text-[10px] text-slate-400" data-id="element-841" data-mp="1fqqy9bw45vhl">Radio IP</span>
            </div>
          </div>

          <div className="pt-2.5 mt-2.5 border-t border-slate-800/70 space-y-1" data-id="element-842" data-mp="1kp0uzqxhl45b">
            <div className="flex items-center gap-2" data-id="element-843" data-mp="1pgaiakvkbve1">
              <span className="h-[2px] w-6 bg-cyan-300 flex-shrink-0" data-id="element-844" data-mp="1pgsp2bfups07" />
              <span className="text-[10px] text-slate-400" data-id="element-845" data-mp="1ufn6pa231aiq">Metro hop</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-846" data-mp="ws4vztov1yc3">
              <span className="h-[2px] w-6 flex-shrink-0" style={{
              backgroundImage: 'repeating-linear-gradient(90deg,#94a3b8 0 4px,transparent 4px 7px)'
            }} data-id="element-847" data-mp="wtpu1tadvzep" />
              <span className="text-[10px] text-slate-400" data-id="element-848" data-mp="1asfot29qdmzw">Transit hop</span>
            </div>
          </div>
        </div>

        {/* TOP-LEFT BADGES */}
        <div className="absolute top-7 left-7 z-[400] flex flex-col gap-2 pointer-events-none" data-id="element-849" data-mp="2i7as5cgkrtdm">
          <div className="glass-panel rounded-xl px-3 py-2 min-w-[126px]" data-id="element-850" data-mp="2m8e61tf2bfc7">
            <div className="text-[10px] text-slate-500 uppercase tracking-[0.12em] font-semibold" data-id="element-851" data-mp="2n408w54c2lqn">
              Route records
            </div>
            <div className="text-sm font-bold text-cyan-200 font-mono" data-id="element-852" data-mp="2vw251r7ddfv4">
              {selectedRegional ? routeRecordCount.toLocaleString() : '—'}
            </div>
          </div>

          <div className="glass-panel rounded-xl px-3 py-2" data-id="element-853" data-mp="245e68lt64mkm">
            <div className="text-[10px] text-slate-500 uppercase tracking-[0.12em] font-semibold" data-id="element-854" data-mp="246z2cfv4sdkl">
              Visible sites
            </div>
            <div className="text-sm font-bold text-white font-mono" data-id="element-855" data-mp="2d6cbf7fcf04s">
              {searchTerm && searchCenters.length ? visibleNodes.length.toLocaleString() : '—'}
            </div>
            {searchTerm && searchCenters.length > 0 && <div className="text-[10px] text-slate-500 font-mono mt-0.5" data-id="element-856" data-mp="3onc1rxi4r4w6">
                {searchCenters.length} match · radius {NEARBY_RADIUS_KM} km
              </div>}
          </div>

          {!showLabels && searchTerm && searchCenters.length > 0 && <div className="glass-panel rounded-xl px-3 py-2 max-w-[190px]" data-id="element-857" data-mp="1cye9w4nre6g4">
              <div className="text-[10px] text-amber-300/90 leading-snug" data-id="element-858" data-mp="186e6u3ce6xfb">
                Zoom in (≥ {LABEL_MIN_ZOOM}) untuk menampilkan label Site ID NE
              </div>
            </div>}
        </div>
      </div>

      {/* DRAWER — keterangan dipertahankan */}
      <LinkRouteDrawer site={drawerSite} isOpen={!!drawerSite} onClose={() => setDrawerSite(null)} onTraceToMetro={showTraceToMetro} onOpenSite={openDetail} data-id="element-859" data-mp="u7o5gbijibl5" />
    </div>;
}